import React, { useState } from 'react';
import { Mail, Phone, MapPin, MessageCircle, Clock } from 'lucide-react';
import { useSection } from '../contexts/ContentContext';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const data = useSection('contact', {
    eyebrow: 'Begin a Conversation',
    heading: 'Visit the Atelier',
    intro: "We welcome inquiries from patrons, press, and fellow admirers of the craft. Tell us about the occasion — we'll guide you from the first thread to the final flourish.",
    email: 'info@silkandsentiment.com',
    phone: '+91 9151 060 995',
    address: 'Lucknow, Uttar Pradesh\nBy Appointment Only',
    hours: 'Monday – Saturday\n11:00 am – 7:00 pm IST',
    whatsappNumber: '919151060995',
  });
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'loading', msg: '' });
    try {
      const res = await fetch(`${BACKEND_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message,
          source: 'contact_page',
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      setStatus({
        state: 'success',
        msg: 'Thank you. Our atelier will reach out within 48 hours.',
      });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setStatus({
        state: 'error',
        msg: 'Something went wrong. Please email us at info@silkandsentiment.com.',
      });
    }
  };

  return (
    <section style={styles.page} data-testid="contact-page">
      <SEO
        title={data.heading || 'Contact'}
        description={data.intro}
        path="/contact"
      />
      <div className="luxury-container" style={styles.container}>
        <header style={styles.header}>
          <p style={styles.eyebrow}>{data.eyebrow}</p>
          <h1 className="heading-1" style={styles.title}>
            {data.heading}
          </h1>
          <p className="body-large" style={styles.lead}>
            {data.intro}
          </p>
        </header>

        <div style={styles.grid}>
          {/* Contact Info */}
          <aside style={styles.info}>
            <InfoItem
              Icon={Mail}
              label="Email"
              value={data.email}
              href={`mailto:${data.email}`}
              testId="contact-email"
            />
            <InfoItem
              Icon={MessageCircle}
              label="WhatsApp"
              value={data.phone}
              href={`https://wa.me/${data.whatsappNumber || (data.phone || '').replace(/\D/g, '')}`}
              testId="contact-whatsapp"
            />
            <InfoItem
              Icon={Phone}
              label="Phone"
              value={data.phone}
              href={`tel:${(data.phone || '').replace(/\s/g, '')}`}
              testId="contact-phone"
            />
            <InfoItem
              Icon={MapPin}
              label="Atelier"
              value={data.address}
              testId="contact-address"
            />
            <InfoItem
              Icon={Clock}
              label="Hours"
              value={data.hours}
              testId="contact-hours"
            />
          </aside>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            style={styles.form}
            data-testid="contact-form"
          >
            <div style={styles.formRow}>
              <Field
                label="Your Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                testId="contact-input-name"
              />
              <Field
                label="Email Address"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                testId="contact-input-email"
              />
            </div>
            <Field
              label="Phone / WhatsApp"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
              minLength={10}
              testId="contact-input-phone"
            />
            <Field
              label="Your Message"
              name="message"
              as="textarea"
              value={form.message}
              onChange={handleChange}
              required
              testId="contact-input-message"
            />

            <button
              type="submit"
              className="btn-primary"
              style={styles.submit}
              disabled={status.state === 'loading'}
              data-testid="contact-submit-btn"
            >
              {status.state === 'loading' ? 'Sending…' : 'Send Inquiry'}
            </button>

            {status.state === 'success' && (
              <p style={styles.success} data-testid="contact-success">
                ✓ {status.msg}
              </p>
            )}
            {status.state === 'error' && (
              <p style={styles.error} data-testid="contact-error">
                {status.msg}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

const InfoItem = ({ Icon, label, value, href, testId }) => {
  const content = (
    <>
      <div style={infoStyles.iconWrap}>
        <Icon size={16} color="var(--accent-gold)" />
      </div>
      <div>
        <p style={infoStyles.label}>{label}</p>
        <p style={infoStyles.value}>{value}</p>
      </div>
    </>
  );

  return href ? (
    <a href={href} style={infoStyles.row} data-testid={testId}>
      {content}
    </a>
  ) : (
    <div style={infoStyles.row} data-testid={testId}>
      {content}
    </div>
  );
};

const Field = ({ label, name, as, testId, ...rest }) => {
  const Component = as === 'textarea' ? 'textarea' : 'input';
  return (
    <label style={fieldStyles.label}>
      <span style={fieldStyles.labelText}>{label}</span>
      <Component
        name={name}
        style={as === 'textarea' ? fieldStyles.textarea : fieldStyles.input}
        data-testid={testId}
        {...rest}
      />
    </label>
  );
};

const styles = {
  page: {
    padding: '140px 0 100px',
    backgroundColor: 'var(--bg-primary)',
    minHeight: '100vh',
  },
  container: {},
  header: {
    textAlign: 'center',
    maxWidth: '720px',
    margin: '0 auto 72px',
  },
  eyebrow: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
    marginBottom: '16px',
  },
  title: {
    marginBottom: '20px',
  },
  lead: {
    color: 'var(--text-secondary)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.5fr',
    gap: '64px',
    alignItems: 'start',
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    background: 'var(--bg-secondary)',
    padding: '40px',
    border: '1px solid var(--border-light)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
  },
  submit: {
    marginTop: '12px',
    alignSelf: 'flex-start',
    minWidth: '200px',
  },
  success: {
    marginTop: '12px',
    padding: '16px',
    background: 'rgba(201, 169, 97, 0.1)',
    border: '1px solid rgba(201, 169, 97, 0.3)',
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    color: 'var(--accent-gold)',
  },
  error: {
    marginTop: '12px',
    padding: '16px',
    background: 'rgba(180, 60, 60, 0.08)',
    border: '1px solid rgba(180, 60, 60, 0.25)',
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    color: '#a33',
  },
};

const infoStyles = {
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    textDecoration: 'none',
    color: 'inherit',
    paddingBottom: '24px',
    borderBottom: '1px solid var(--border-light)',
    transition: 'color 0.3s ease',
  },
  iconWrap: {
    flexShrink: 0,
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'rgba(201, 169, 97, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid rgba(201, 169, 97, 0.2)',
  },
  label: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '10px',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
    marginBottom: '4px',
  },
  value: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    color: 'var(--text-primary)',
    lineHeight: '1.6',
    whiteSpace: 'pre-line',
  },
};

const fieldStyles = {
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  labelText: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
  },
  input: {
    padding: '14px 16px',
    border: '1px solid var(--border-light)',
    background: 'var(--bg-primary)',
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    color: 'var(--text-primary)',
    outline: 'none',
    transition: 'border-color 0.3s ease',
  },
  textarea: {
    padding: '14px 16px',
    border: '1px solid var(--border-light)',
    background: 'var(--bg-primary)',
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    color: 'var(--text-primary)',
    outline: 'none',
    minHeight: '160px',
    resize: 'vertical',
  },
};

const responsive = document.createElement('style');
responsive.textContent = `
  [data-testid="contact-page"] input:focus,
  [data-testid="contact-page"] textarea:focus {
    border-color: var(--accent-gold) !important;
  }
  [data-testid="contact-page"] a[data-testid^="contact-"]:hover p:last-child {
    color: var(--accent-gold) !important;
  }
  @media (max-width: 900px) {
    [data-testid="contact-page"] > div > div:last-child {
      grid-template-columns: 1fr !important;
      gap: 32px !important;
    }
    [data-testid="contact-form"] > div:first-child {
      grid-template-columns: 1fr !important;
    }
  }
`;
document.head.appendChild(responsive);

export default ContactPage;
